/**
 * The feature list that works like the phone app (layout Z, and a short version inside W): each row
 * is something CherryClipz does, with a looping clip on the left, the words in the middle and a
 * cherrypick circle on the right; the app's bottom bar holds your picks, Play and Get the app.
 * Play turns the ticked rows into one reel in a phone-sized player.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mark, StoreBadges } from "./kit";
import { storeForThisPhone } from "./chrome";
import { APP_STORE_URL, Z, ZFeature, Z_FEATURES, Z_MORE, storeLink } from "../../constants/homeLayoutsText";
import { fmtTime } from "./filmData";

const mediaOf = (f: ZFeature) => f.media || `/home/z/${f.id}`;
const GROUPS: ZFeature["group"][] = ["phone", "web", "ai", "pro"];

/** One clip that plays only while it is on screen (so 24 loops never run at once). */
const RowMedia: React.FC<{ f: ZFeature }> = ({ f }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === "undefined") return;
    let still = false;
    try {
      still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      /* ignore */
    }
    if (still) return;
    const io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            if (v.preload !== "auto") v.preload = "auto";
            const p = v.play();
            if (p && p.catch) p.catch((): void => undefined);
          } else v.pause();
        }),
      { threshold: 0.35 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} src={`${mediaOf(f)}.mp4`} poster={`${mediaOf(f)}.jpg`} muted loop playsInline preload="none" aria-label={f.title} />;
};

/** The reel: the picked features, one after another, like stories. */
export const Reel: React.FC<{ items: ZFeature[]; onClose: () => void; tag: string }> = ({ items, onClose, tag }) => {
  const [k, setK] = useState(0);
  const [p, setP] = useState(0);
  const vRef = useRef<HTMLVideoElement>(null);
  const done = k >= items.length;
  const cur = done ? null : items[k];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setK((x) => Math.min(items.length, x + 1));
      if (e.key === "ArrowLeft") setK((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length, onClose]);

  useEffect(() => {
    setP(0);
    const v = vRef.current;
    if (!v || !cur) return;
    v.currentTime = 0;
    const pr = v.play();
    if (pr && pr.catch) pr.catch((): void => undefined);
  }, [k, cur]);

  const onTime = () => {
    const v = vRef.current;
    if (!v || !cur) return;
    const len = Math.min(cur.dur, v.duration || cur.dur);
    setP(Math.min(1, v.currentTime / len));
    if (v.currentTime >= len - 0.05) setK((x) => x + 1);
  };

  return (
    <div className="z-reel" role="dialog" aria-modal="true" aria-label={Z.reelTitle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="z-reel-phone">
        <div className="z-bars">
          {items.map((f, i) => (
            <i key={f.id}>
              <b style={{ width: `${i < k ? 100 : i === k ? p * 100 : 0}%` }} />
            </i>
          ))}
        </div>
        {cur ? (
          <>
            <video ref={vRef} key={cur.id} src={`${mediaOf(cur)}.mp4`} poster={`${mediaOf(cur)}.jpg`} muted playsInline autoPlay onTimeUpdate={onTime} onEnded={() => setK((x) => x + 1)} />
            <div className="z-reel-cap">
              <span className="n">{k + 1}</span>
              {cur.title}
            </div>
            <button type="button" className="z-tap prev" aria-label="Previous" onClick={() => setK((x) => Math.max(0, x - 1))} />
            <button type="button" className="z-tap next" aria-label="Next" onClick={() => setK((x) => x + 1)} />
          </>
        ) : (
          <div className="z-end">
            <Mark size={84} />
            <h3>
              {Z.endTitle1} <span className="rw">{Z.endTitle2}</span>
            </h3>
            <StoreBadges size="md" onDark tag={`${tag}-reel`} />
            <button type="button" className="h3-btn ghost sm" onClick={() => setK(0)}>
              {Z.replay}
            </button>
          </div>
        )}
      </div>
      <button type="button" className="z-close" aria-label={Z.close} onClick={onClose}>
        ×
      </button>
    </div>
  );
};


export const FeatureList: React.FC<{ ids?: string[]; preset: string[]; full?: boolean; tag: string; title?: string; sticky?: boolean }> = ({ ids, preset, full = false, tag, title, sticky = true }) => {
  const pool = useMemo(() => (ids ? ids.map((id) => Z_FEATURES.find((f) => f.id === id)).filter((f): f is ZFeature => !!f) : Z_FEATURES), [ids]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<ZFeature["group"] | "all">("all");
  const [picks, setPicks] = useState<string[]>(preset);
  const [reel, setReel] = useState(false);
  const byId = useMemo(() => Object.fromEntries(Z_FEATURES.map((f) => [f.id, f])), []);
  const store = useMemo(() => storeLink(storeForThisPhone() || APP_STORE_URL, `${tag}-bar`), [tag]);
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return pool.filter((f) => (group === "all" || f.group === group) && (!n || `${f.title} ${f.body}`.toLowerCase().includes(n)));
  }, [q, group, pool]);
  // Rows the visitor ticked themselves open up (the preset ones stay closed, so the page doesn't
  // load with three big players); unticking closes it again.
  const [opened, setOpened] = useState<string[]>([]);
  const toggle = (id: string) => {
    const on = !picks.includes(id);
    setPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    setOpened((o) => (on ? [...o.filter((x) => x !== id), id] : o.filter((x) => x !== id)));
  };
  const allShownPicked = shown.length > 0 && shown.every((f) => picks.includes(f.id));
  const total = picks.reduce((s, id) => s + (byId[id]?.dur || 0), 0);
  const groups = full ? GROUPS : (["all"] as const);

  const row = (f: ZFeature) => {
    const k = picks.indexOf(f.id);
    return (
      <div key={f.id} className={`z-row${k >= 0 ? " on" : ""}`}>
        <button type="button" className="z-media" onClick={() => toggle(f.id)} aria-label={`${k >= 0 ? "Remove" : "Pick"}: ${f.title}`}>
          <RowMedia f={f} />
        </button>
        <div className="z-text" onClick={() => toggle(f.id)}>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
          <div className="z-meta">
            {f.where.map((w) => (
              <span key={w} className={`w-${w.toLowerCase()}`}>
                {w}
              </span>
            ))}
            {f.soon && <span className="soon">{Z.soon}</span>}
            <em>{f.dur.toFixed(1)}s</em>
          </div>
        </div>
        <button type="button" className="z-circle" aria-pressed={k >= 0} aria-label={k >= 0 ? `Picked ${k + 1}: remove` : `Pick: ${f.title}`} onClick={() => toggle(f.id)}>
          {k >= 0 ? (
            <>
              <Mark size={34} />
              <b>{k + 1}</b>
            </>
          ) : null}
        </button>
        {/* Ticked on the full page: the row opens with the clip big and where to use it; unticking closes it. */}
        {full && k >= 0 && opened.includes(f.id) && (
          <div className="z-more">
            <div className="z-more-media">
              <RowMedia f={f} />
            </div>
            <a
              className="h3-btn sm"
              href={Z_MORE[f.group].href}
              onClick={(e) => {
                if (Z_MORE[f.group].href === "#top") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              {Z_MORE[f.group].label}
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`zf${full ? " full" : ""}`}>
      {full && (
        <div className="z-head">
          <h2>{title || Z.listTitle(pool.length)}</h2>
          <label className="z-search" htmlFor="z-q">
            <svg viewBox="0 0 16 16" fill="none" strokeWidth="1.8" aria-hidden>
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" strokeLinecap="round" />
            </svg>
            <input id="z-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={Z.search} autoComplete="off" />
          </label>
          <div className="z-chips">
            <button type="button" aria-pressed={group === "all"} onClick={() => setGroup("all")}>
              {Z.all}
            </button>
            {GROUPS.map((g) => (
              <button key={g} type="button" aria-pressed={group === g} onClick={() => setGroup(g)}>
                {Z.groups[g]}
              </button>
            ))}
            <span className="sep" />
            <button
              type="button"
              className="ghostchip"
              onClick={() => setPicks((p) => (allShownPicked ? p.filter((id) => !shown.some((f) => f.id === id)) : [...p, ...shown.map((f) => f.id).filter((id) => !p.includes(id))]))}
            >
              {allShownPicked ? Z.deselectAll : Z.selectAll}
            </button>
          </div>
        </div>
      )}
      <div className="z-list">
        {!shown.length && <p className="z-none">{Z.noMatch(q)}</p>}
        {groups.map((g) => {
          const rows = g === "all" ? shown : shown.filter((f) => f.group === g);
          if (!rows.length) return null;
          return (
            <React.Fragment key={g}>
              {g !== "all" && (
                <div className="z-gap">
                  <span>{Z.groups[g]}</span>
                  <i />
                  <span>{Z.groupCount(rows.length)}</span>
                </div>
              )}
              {rows.map(row)}
            </React.Fragment>
          );
        })}
      </div>
      <div className={`z-bar${sticky ? " sticky" : ""}`}>
        <div className="z-bar-top">
          <ol className="z-strip">
            {picks.map((id, i) => (
              <li key={id}>
                <img src={`${mediaOf(byId[id])}.jpg`} alt="" />
                <span>{i + 1}</span>
              </li>
            ))}
            {!picks.length && <li className="z-strip-empty">{Z.emptyBar}</li>}
          </ol>
          <div className="z-tot">
            <b>{fmtTime(total)}</b>
            <span>{Z.clips(picks.length)}</span>
            <button type="button" onClick={() => setPicks([])} disabled={!picks.length}>
              {Z.clear}
            </button>
          </div>
        </div>
        <div className="z-bar-btns">
          <button type="button" className="h3-btn ghost" disabled={!picks.length} onClick={() => setReel(true)}>
            <span className="tri" />
            {Z.play}
          </button>
          <a className="h3-btn cherry" href={store} target="_blank" rel="noreferrer">
            {Z.getApp}
          </a>
        </div>
      </div>
      {reel && <Reel items={picks.map((id) => byId[id]).filter(Boolean)} tag={tag} onClose={() => setReel(false)} />}
    </div>
  );
};

export const FEATURES_CSS = `
.h3 .zf,.h3 .z-reel{--app:#1c1619;--app2:#251d21;--ring:#4a3f43}
.h3-z .z-hero{padding-block:clamp(40px,6vw,80px) clamp(36px,4vw,56px);text-align:center;background:radial-gradient(60% 80% at 50% 0%,rgba(230,29,80,.2),transparent 70%)}
.h3-z .z-hero .wrap{display:grid;justify-items:center;gap:18px}
.h3-z .z-hero h1{font-weight:800;font-size:clamp(44px,7.2vw,98px);line-height:.95;letter-spacing:-.05em}
.h3-z .z-hero .lede{max-width:54ch}
.h3-z .z-up{width:min(640px,100%)}
.h3-z .z-up .ccs.inline{margin-inline:auto;text-align:center}
.h3-z .z-up .ccs.inline .under{justify-content:center}
.h3 .z-more{grid-column:1/-1;display:grid;justify-items:start;gap:12px;padding:4px 0 8px}
.h3 .z-more-media{width:min(420px,100%);border-radius:14px;overflow:hidden;background:#000}
.h3 .z-more-media video{width:100%;display:block}
.h3-z .z-hero .h3-stores{justify-content:center}

.h3-z .z-app{padding-block:0 clamp(64px,8vw,100px)}
.h3 .z-col{max-width:880px;margin-inline:auto;padding-inline:clamp(12px,3vw,24px)}
.h3 .zf.full .z-head{position:sticky;top:calc(68px + env(safe-area-inset-top,0px));z-index:20;background:var(--bg);padding:14px 0 12px;display:grid;gap:12px;border-bottom:1px solid var(--line)}
.h3 .zf .z-head h2{font:700 clamp(26px,3.2vw,36px)/1.1 var(--fd);letter-spacing:-.03em}
.h3 .zf .z-search{display:flex;align-items:center;gap:10px;height:50px;border-radius:999px;border:1.5px solid var(--ring);padding:0 18px;background:transparent}
.h3 .zf .z-search svg{width:18px;height:18px;stroke:var(--ink3);flex:none}
.h3 .zf .z-search input{flex:1;min-width:0;border:0;background:transparent;outline:none;color:var(--ink);font:500 17px/1 var(--fd)}
.h3 .zf .z-search input::placeholder{color:var(--ink3)}
.h3 .zf .z-search:focus-within{border-color:var(--cherry)}
.h3 .zf .z-chips{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;align-items:center}
.h3 .zf .z-chips::-webkit-scrollbar{display:none}
.h3 .zf .z-chips button{flex:none;height:36px;padding:0 15px;border-radius:999px;border:1.5px solid var(--ring);background:transparent;font:600 15px/1 var(--fd);color:var(--ink)}
.h3 .zf .z-chips button[aria-pressed="true"]{background:var(--cherry);border-color:var(--cherry);color:#fff}
.h3 .zf .z-chips .sep{width:1px;height:20px;background:var(--line2);flex:none}
.h3 .zf .z-list{display:grid;gap:12px;padding-top:6px}
.h3 .zf .z-none{padding:40px 0;text-align:center;color:var(--ink3)}
.h3 .zf .z-gap{display:flex;align-items:center;gap:12px;padding:22px 4px 4px;font:500 12px/1 var(--fm);letter-spacing:.12em;text-transform:uppercase;color:var(--ink3)}
.h3 .zf .z-gap i{flex:1;height:1px;background:repeating-linear-gradient(90deg,var(--line2) 0 6px,transparent 6px 12px)}
.h3 .zf .z-row{display:grid;grid-template-columns:clamp(112px,19vw,168px) minmax(0,1fr) 64px;gap:clamp(14px,2.4vw,24px);align-items:center;padding:12px;border-radius:24px;background:var(--app);border:1.5px solid transparent;transition:background-color .2s,border-color .2s}
.h3 .zf .z-row.on{border-color:rgba(230,29,80,.55);background:linear-gradient(90deg,rgba(230,29,80,.1),var(--app) 60%)}
.h3 .zf .z-media{display:block;padding:0;border:0;border-radius:16px;overflow:hidden;aspect-ratio:9/16;background:#000;cursor:pointer}
.h3 .zf .z-media video{width:100%;height:100%;object-fit:cover;display:block}
.h3 .zf .z-text{min-width:0;cursor:pointer}
.h3 .zf .z-text h3{font:700 clamp(19px,2.2vw,26px)/1.18 var(--fd);letter-spacing:-.02em;color:var(--ink)}
.h3 .zf .z-text p{margin-top:8px;font-size:clamp(15px,1.5vw,17px);line-height:1.45;color:var(--ink2);max-width:46ch}
.h3 .zf .z-meta{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:12px;font:500 12.5px/1 var(--fm)}
.h3 .zf .z-meta span{height:24px;padding:0 9px;border-radius:999px;display:inline-flex;align-items:center;background:#f4ede4;color:#17110f;font:700 12.5px/1 var(--fd)}
.h3 .zf .z-meta span.w-ai{background:#8ec5ff}.h3 .zf .z-meta span.w-pro{background:#b39cff}.h3 .zf .z-meta span.w-web{background:#ffd166}
.h3 .zf .z-meta span.soon{background:transparent;color:var(--cherry-t);border:1.5px solid currentColor}
.h3 .zf .z-meta em{font-style:normal;color:var(--ink3)}
.h3 .zf .z-circle{justify-self:center;position:relative;width:56px;height:56px;border-radius:50%;border:2.5px solid var(--ring);background:transparent;display:grid;place-items:center;padding:0;transition:border-color .15s,transform .15s}
.h3 .zf .z-circle:hover{border-color:#8a7c80;transform:scale(1.05)}
.h3 .zf .z-circle[aria-pressed="true"]{border-color:transparent;animation:zpop .35s ease}
.h3 .zf .z-circle b{position:absolute;bottom:-20px;left:50%;transform:translateX(-50%);font:800 16px/1 var(--fd);color:var(--cherry-t)}
@keyframes zpop{40%{transform:scale(1.18)}}

.h3 .zf .z-bar.sticky{position:sticky;bottom:calc(12px + env(safe-area-inset-bottom,0px));z-index:25}
.h3 .zf .z-bar{margin-top:20px;background:rgba(17,12,13,.94);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border:1px solid var(--line2);border-radius:24px;padding:12px;display:grid;gap:10px;box-shadow:0 -10px 40px rgba(0,0,0,.45)}
.h3 .zf .z-bar-top{display:flex;align-items:center;gap:12px}
.h3 .zf .z-strip{flex:1;min-width:0;list-style:none;margin:0;padding:0;display:flex;gap:8px;overflow-x:auto;scrollbar-width:none}
.h3 .zf .z-strip li{position:relative;flex:none;width:46px;height:64px;border-radius:9px;overflow:hidden;background:#000}
.h3 .zf .z-strip img{width:100%;height:100%;object-fit:cover;display:block}
.h3 .zf .z-strip li span{position:absolute;left:3px;bottom:3px;min-width:16px;height:16px;border-radius:5px;background:var(--cherry);color:#fff;font:700 10.5px/16px var(--fd);text-align:center}
.h3 .zf .z-strip .z-strip-empty{width:auto;height:64px;background:transparent;display:flex;align-items:center;color:var(--ink3);font-size:15px}
.h3 .zf .z-tot{display:grid;justify-items:end;gap:2px;flex:none}
.h3 .zf .z-tot b{font:800 22px/1 var(--fd)}
.h3 .zf .z-tot span{font-size:13px;color:var(--ink3)}
.h3 .zf .z-tot button{margin-top:4px;height:28px;padding:0 12px;border-radius:999px;border:1.5px solid var(--ring);background:transparent;font:600 13px/1 var(--fd)}
.h3 .zf .z-tot button[disabled]{opacity:.35}
.h3 .zf .z-bar-btns{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.h3 .zf .z-bar-btns .h3-btn{height:52px;font-size:17px}
.h3 .zf:not(.full) .z-row{grid-template-columns:clamp(96px,13vw,124px) minmax(0,1fr) 64px}
@media (max-width:560px){.h3 .zf .z-row{grid-template-columns:96px minmax(0,1fr) 46px;padding:10px;gap:12px}.h3 .zf .z-circle{width:44px;height:44px}.h3 .zf .z-circle img{height:28px!important}.h3 .zf .z-text p{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}}

.h3-z .z-web{border-top:1px solid var(--line);background:var(--bg2)}
.h3-z .z-web-grid{display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);gap:clamp(24px,4vw,64px);align-items:center}
@media (max-width:860px){.h3-z .z-web-grid{grid-template-columns:1fr}}

.h3 .z-reel{position:fixed;inset:0;z-index:13000;background:rgba(8,5,6,.9);display:grid;place-items:center;padding:20px 16px}
.h3 .z-reel-phone{position:relative;height:min(86vh,820px);aspect-ratio:9/16;max-width:100%;border-radius:34px;overflow:hidden;background:#000;border:8px solid #0b0809;box-shadow:0 30px 80px rgba(0,0,0,.6),inset 0 0 0 1px #2e2528}
.h3 .z-reel-phone video{width:100%;height:100%;object-fit:cover;display:block}
.h3 .zf .z-bars{position:absolute;left:10px;right:10px;top:10px;z-index:3;display:flex;gap:4px}
.h3 .zf .z-bars i{flex:1;height:3px;border-radius:2px;background:rgba(255,255,255,.3);overflow:hidden}
.h3 .zf .z-bars b{display:block;height:100%;background:#fff}
.h3 .z-reel-cap{position:absolute;left:14px;right:14px;bottom:18px;z-index:3;display:flex;align-items:center;gap:10px;padding:12px 14px;border-radius:16px;background:rgba(17,12,13,.78);backdrop-filter:blur(8px);font:700 17px/1.25 var(--fd);color:#fff}
.h3 .z-reel-cap .n{flex:none;width:26px;height:26px;border-radius:8px;background:var(--cherry);display:grid;place-items:center;font-size:13px}
.h3 .z-tap{position:absolute;top:0;bottom:90px;z-index:2;border:0;background:transparent}
.h3 .z-tap.prev{left:0;width:30%}.h3 .z-tap.next{right:0;width:70%}
.h3 .z-end{height:100%;display:grid;place-content:center;justify-items:center;gap:20px;padding:24px;text-align:center;background:radial-gradient(70% 50% at 50% 40%,rgba(230,29,80,.25),#0d0a0b 70%)}
.h3 .z-end h3{font:800 34px/1 var(--fd);letter-spacing:-.04em;color:#fff}
.h3 .z-end .h3-stores{justify-content:center}
.h3 .z-close{position:absolute;top:calc(14px + env(safe-area-inset-top,0px));right:14px;width:44px;height:44px;border-radius:50%;border:0;background:rgba(255,255,255,.14);color:#fff;font-size:22px}
`;
